import { useEffect, useMemo, useRef, useCallback } from 'react'
import { Marker, Popup, useMap } from 'react-leaflet'
import { Box, Typography } from '@mui/material'
import Leaflet from 'leaflet'
import { renderToStaticMarkup } from 'react-dom/server'
import { delay } from 'lodash'
import { resetSelectedSearchResult, selectSearchResult, useDispatch } from '@ggs/store'
import { useWindowResize } from '@ggs/hooks'
import icons from '@ggs/ui/icons'
import { LeafletPlace } from '../../../definitions'
import { detailPath } from '../../../local/utils'

/**
 * @typedef {Object} PlaceMarkerProps
 * @property {LeafletPlace} place A Leaflet place.
 * @property {boolean} isSelected Flag to indicate that place is selected.
 */

/**
 * Provides a Leaflet place marker.
 * @param {PlaceMarkerProps} props
 * @return {JSX.Element}
 */
const PlaceMarker = ({ place, isSelected }) => {
  const markerRef = useRef(null)
  const dispatch = useDispatch()
  const windowSize = useWindowResize()
  const isMobile = useMemo(() => windowSize.width <= 576, [windowSize.width])
  const map = useMap()
  // const mapSize = map.getSize()
  const highlightClass = 'highlight'

  const customMarkerIcon = useMemo(() => {
    return Leaflet.divIcon({
      html: renderToStaticMarkup(
        <Box className="leaflet-marker">
          {icons.Marker}
          <span>{place.index}</span>
        </Box>
      ),
      iconSize: [0, 0],
      // On mobile screen display popup below marker icon.
      popupAnchor: [0, isMobile ? 300 : 0],
      className: '',
    })
  }, [isMobile])

  /**
   * Highlight a marker.
   * @param {HTMLDivElement} markerElement
   */
  const highlightMarker = (markerElement) => {
    if (markerElement) {
      markerElement.classList.add(highlightClass)
    }
  }

  /**
   * Clear highlight marker.
   * @param {HTMLDivElement} markerElement
   */
  const clearHighlightMarker = (markerElement) => {
    if (markerElement) {
      markerElement.classList.remove(highlightClass)
    }
  }

  // Auto-open marker popup when marker place is selected and hit initial render.
  useEffect(() => {
    const currentMarker = markerRef.current
    if (currentMarker && isSelected) {
      const selectedMarker = markerRef.current
      if (!selectedMarker.isPopupOpen()) {
        // Wait that marker is ready, as no event exists for this we delay 500ms.
        delay(() => {
          selectedMarker.openPopup()
        }, 500)
      }
    }
  }, [markerRef])

  /**
   * When popup is opened, set current place as selected.
   */
  const handlePopupOpen = useCallback(() => {
    dispatch(selectSearchResult(Number(place.id)))
    storeDistance()
  }, [])

  /**
   * Store distance of place in local distance
   */
  const storeDistance = () => {
    const distances = localStorage.getItem('resultDistances')
    const distanceMap = new Map(distances ? Object.entries(JSON.parse(distances)) : undefined)
    distanceMap.set(`${place.id}`, place.distance)
    localStorage.setItem('resultDistances', JSON.stringify(Object.fromEntries(distanceMap)))
  }

  /**
   * When popup is closed, clear any selected place.
   */
  const handlePopupClose = useCallback(() => {
    dispatch(resetSelectedSearchResult())
  }, [])

  // Control marker highlight when marker is selected/un-selected.
  const markerElement = markerRef.current ? markerRef.current.getElement() : null
  useEffect(() => {
    if (markerElement) {
      if (isSelected) {
        highlightMarker(markerElement)
      } else {
        clearHighlightMarker(markerElement)
      }
    }
  }, [markerElement, isSelected])

  return useMemo(
    () => (
      <Marker
        eventHandlers={{
          popupopen: handlePopupOpen,
          popupclose: handlePopupClose,
        }}
        ref={markerRef}
        position={[place.latitude, place.longitude]}
        icon={customMarkerIcon}
        title={place.label}
      >
        <Popup
          className={'results-map__popup'}
          keepInView={true}
          closeOnClick={true}
          closeOnEscapeKey={true}
        >
          <Typography component="a" className="title" href={detailPath(parseInt(place.id))}>
            {place.index && `${place.index}.`} {place.label}
          </Typography>
          {place.popupContent}
        </Popup>
      </Marker>
    ),
    [isMobile]
  )
}

export default PlaceMarker
